import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, Trash2, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { useSiteContent } from "@/context/SiteContentContext";
import { MENU_KEYS as K, DEFAULT_SHOP_LINKS, defaultNavLinks, parseLinks } from "@/lib/menuLinks";

const validUrl = (u) => /^(\/|https?:\/\/|mailto:|tel:)/i.test(u.trim());

function LinkListEditor({ title, hint, prefix, visible, links, onToggle, onSave, busy }) {
  const [rows, setRows] = useState(links);
  const [draft, setDraft] = useState({ name: "", url: "" });
  const linksKey = JSON.stringify(links);
  useEffect(() => { setRows(JSON.parse(linksKey)); }, [linksKey]);
  const upd = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setRows((r) => { const n = [...r]; const j = i + d; if (j < 0 || j >= n.length) return r; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const add = () => {
    if (!draft.name.trim() || !validUrl(draft.url)) return toast.error("Enter a name and a URL starting with / or https://");
    setRows((r) => [...r, { name: draft.name.trim(), url: draft.url.trim() }]);
    setDraft({ name: "", url: "" });
  };
  const save = () => {
    const clean = rows.map((r) => ({ name: r.name.trim(), url: r.url.trim() }));
    if (clean.some((r) => !r.name || !validUrl(r.url))) return toast.error("Every link needs a name and a URL starting with / or https://");
    onSave(clean);
  };

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-2xl" data-testid={`${prefix}-card`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg">{title}</div>
          <div className="text-xs text-gray-500">{hint}</div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid={`${prefix}-visible-toggle`} checked={visible} onCheckedChange={onToggle} disabled={busy} />
          {visible ? "Shown on website" : "Hidden"}
        </label>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-gray-500">No links yet.</div>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center" data-testid={`${prefix}-row-${i}`}>
            <Input data-testid={`${prefix}-name-${i}`} value={r.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Name" className="h-9" />
            <Input data-testid={`${prefix}-url-${i}`} value={r.url} onChange={(e) => upd(i, { url: e.target.value })} placeholder="/category/kids" className="h-9 col-span-2 sm:col-span-1 order-3 sm:order-none" />
            <div className="flex items-center gap-0.5">
              <button type="button" data-testid={`${prefix}-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-gray-500 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" data-testid={`${prefix}-down-${i}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1.5 text-gray-500 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" data-testid={`${prefix}-remove-${i}`} onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="p-1.5 text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 p-3 rounded-xl bg-gray-50 border border-dashed border-gray-200">
        <Input data-testid={`${prefix}-new-name`} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="New link name" className="h-9 bg-white" />
        <Input data-testid={`${prefix}-new-url`} value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="/category/... or https://..." className="h-9 bg-white" />
        <Button type="button" data-testid={`${prefix}-add`} variant="outline" onClick={add} className="h-9 rounded-full text-xs"><PlusCircle className="h-3.5 w-3.5 mr-1" /> Add link</Button>
      </div>
      <Button data-testid={`${prefix}-save`} onClick={save} disabled={busy} className="mt-4 rounded-full bg-[#F48E2E] hover:bg-[#D97815]">{busy ? "Saving…" : "Save links"}</Button>
    </div>
  );
}

export default function AdminMenuLinksCard() {
  const { content, refresh } = useSiteContent();
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => setCats([])); }, []);
  if (!cats || !Object.keys(content).length) return null;

  const put = async (pairs, msg) => {
    setBusy(true);
    try {
      for (const [key, value] of pairs) await api.patch(`/admin/site-content/${key}`, { value });
      await refresh();
      toast.success(msg);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const navLinks = parseLinks(content[K.navLinks], defaultNavLinks(cats));
  const shopLinks = parseLinks(content[K.shopLinks], DEFAULT_SHOP_LINKS);

  return (
    <div className="mb-8 grid lg:grid-cols-2 gap-4" data-testid="admin-menu-links">
      <LinkListEditor title="Navbar links" hint="Top menu on desktop and the phone menu. Category links keep their sub-category dropdown."
        prefix="navlinks" visible={content[K.navVisible] !== "false"} links={navLinks} busy={busy}
        onToggle={(v) => put([[K.navVisible, v ? "true" : "false"]], v ? "Navbar links shown" : "Navbar links hidden")}
        onSave={(l) => put([[K.navLinks, JSON.stringify(l)]], "Navbar links saved")} />
      <LinkListEditor title="Footer Shop section" hint="The Shop column in the site footer."
        prefix="shoplinks" visible={content[K.shopVisible] !== "false"} links={shopLinks} busy={busy}
        onToggle={(v) => put([[K.shopVisible, v ? "true" : "false"]], v ? "Shop section shown" : "Shop section hidden")}
        onSave={(l) => put([[K.shopLinks, JSON.stringify(l)]], "Shop links saved")} />
    </div>
  );
}
